import { Loading } from "@components/LoadingIndicator";
import { useAvailableGivingCredit } from "@components/donate/hooks";
import { PageSection } from "@components/layout/PageSection";
import React, { useContext } from "react";

import { AuthContext } from "src/context/AuthContext";
import { AuthStatus } from "src/context/AuthContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { BecomeAnAdminCard } from "src/pages/Nonprofit/BecomeAnAdminCard";
import { NonprofitPageV3AboutCard } from "src/pages/Nonprofit/NonprofitPageV3/NonprofitPageV3AboutCard";
import { NonprofitPageV3HeaderCard } from "src/pages/Nonprofit/NonprofitPageV3/NonprofitPageV3HeaderCard";
import { DonateDafCard } from "src/pages/Nonprofit/UnclaimedNonprofitPage/DonateDafCard";
import { FundAccountCard } from "src/pages/Nonprofit/UnclaimedNonprofitPage/FundAccountCard";
import { GetStartedCTACard } from "src/pages/Nonprofit/UnclaimedNonprofitPage/GetStartedCTACard";
import { ShareProfileCard } from "src/pages/Nonprofit/UnclaimedNonprofitPage/ShareProfileCard";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";

interface UnclaimedNonprofitPageProps {
  nonprofit: ContextNonprofit;
}

export const UnclaimedNonprofitPage = ({
  nonprofit,
}: UnclaimedNonprofitPageProps) => {
  const authState = useContext(AuthContext);

  const { availableGivingCredit } = useAvailableGivingCredit({
    isLoggedIn: authState.status === AuthStatus.LOGGED_IN,
  });

  if (authState.status === AuthStatus.LOADING) {
    return <LoadingView />;
  }

  if (authState.status === AuthStatus.LOGGED_OUT) {
    return <LoggedOutView nonprofit={nonprofit} />;
  }

  if (availableGivingCredit?.gt(0)) {
    return <LoggedInDAFView nonprofit={nonprofit} />;
  }

  return <LoggedInNoDAFView nonprofit={nonprofit} />;
};

const LoadingView = () => (
  <PageSection
    css={{ paddingTop: spacing.l }}
    contentCss={[verticalStackCss.xl, { justifyContent: "center" }]}
  >
    <Loading />
  </PageSection>
);

const LoggedOutView = (props: UnclaimedNonprofitPageProps) => (
  <PageSection
    css={{ paddingTop: spacing.l }}
    contentCss={[verticalStackCss.xl, { alignItems: "center" }]}
  >
    <GetStartedCTACard nonprofit={props.nonprofit} />
    <ShareProfileCard nonprofit={props.nonprofit} />
  </PageSection>
);

const LoggedInNoDAFView = (props: UnclaimedNonprofitPageProps) => (
  <PageSection
    css={{ paddingTop: spacing.l }}
    contentCss={[
      {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: spacing.l,
      },
      cssForMediaSize({
        min: MediaSize.X_LARGE,
        css: { flexDirection: "row", alignItems: "flex-start" },
      }),
    ]}
  >
    <GetStartedCTACard
      css={cssForMediaSize({
        min: MediaSize.X_LARGE,
        css: { flex: "2 1 0" },
      })}
      nonprofit={props.nonprofit}
    />
    {!!props.nonprofit.donationsEnabled && (
      <FundAccountCard
        css={cssForMediaSize({
          min: MediaSize.X_LARGE,
          css: { flex: "1 1 0" },
        })}
        nonprofit={props.nonprofit}
      />
    )}
  </PageSection>
);

const LoggedInDAFView = ({ nonprofit }: UnclaimedNonprofitPageProps) => (
  <PageSection css={{ paddingTop: spacing.l }} contentCss={verticalStackCss.l}>
    <NonprofitPageV3HeaderCard nonprofit={nonprofit} />
    <div
      css={[
        {
          display: "flex",
          flexDirection: "column",
          gap: spacing.l,
        },
        cssForMediaSize({
          min: MediaSize.X_LARGE,
          css: { flexDirection: "row", alignItems: "flex-start" },
        }),
      ]}
    >
      <div
        css={[
          { display: "flex", flexDirection: "column", gap: spacing.l },
          cssForMediaSize({
            min: MediaSize.X_LARGE,
            css: { flex: "2 1 0" },
          }),
        ]}
      >
        <NonprofitPageV3AboutCard nonprofit={nonprofit} />
        <BecomeAnAdminCard
          css={[
            { display: "none" },
            cssForMediaSize({
              min: MediaSize.X_LARGE,
              css: { display: "block" },
            }),
          ]}
          nonprofit={nonprofit}
        />
      </div>
      <DonateDafCard
        nonprofit={nonprofit}
        css={[
          { display: "flex", flexDirection: "column", gap: spacing.l },
          cssForMediaSize({
            min: MediaSize.X_LARGE,
            css: { flex: "1 1 0" },
          }),
        ]}
      />
      <BecomeAnAdminCard
        css={[
          { display: "block" },
          cssForMediaSize({
            min: MediaSize.X_LARGE,
            css: { display: "none" },
          }),
        ]}
        nonprofit={nonprofit}
      />
    </div>
  </PageSection>
);
